import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Button, Text, Spinner, Input, Textarea, Switch,
  Combobox, Option,
  makeStyles, mergeClasses, tokens, MessageBar, MessageBarBody,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogContent, DialogActions,
} from '@fluentui/react-components'
import { ArrowLeft24Regular, Save24Regular, PersonSearch24Regular, Delete24Regular, DocumentCopy24Regular } from '@fluentui/react-icons'
import { ChevronUp20Regular, ChevronDown20Regular } from '@fluentui/react-icons'
import { contractorsService, ContractorForm } from '../../services/contractorsService'
import { referenceDataService } from '../../services/referenceDataService'
import { NativeDateInput } from '../../components/NativeDateInput'
import { CzPhoneInput } from '../../components/CzPhoneInput'
import { employeesService } from '../../services/employeesService'
import { searchEntraUsers } from '../../services/graphService'
import { useAuth } from '../../auth/useAuth'
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog'

const IS_MOCK = import.meta.env.VITE_MOCK_AUTH === 'true'

const useStyles = makeStyles({
  pageHeader: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '12px',
    boxShadow: tokens.shadow4,
    marginBottom: '16px',
  },
  changedStripe: { height: '3px', backgroundColor: tokens.colorBrandBackground, borderRadius: '12px 12px 0 0' },
  cardHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 24px', cursor: 'pointer',
    ':hover': { backgroundColor: tokens.colorNeutralBackground2 },
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  cardTitle: { fontSize: tokens.fontSizeBase400, fontWeight: tokens.fontWeightSemibold },
  fieldRow: {
    display: 'grid', gridTemplateColumns: '1fr 1fr',
    borderBottom: `1px solid ${tokens.colorNeutralBackground3}`,
  },
  fieldRowFull: { gridTemplateColumns: '1fr' },
  fieldCell: { padding: '10px 24px 12px', display: 'flex', flexDirection: 'column', gap: '6px' },
  fieldCellBorderRight: { borderRight: `1px solid ${tokens.colorNeutralBackground3}` },
  fieldLabel: {
    fontSize: '11px', fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase', letterSpacing: '0.5px',
    display: 'flex', alignItems: 'center', gap: '5px',
  },
  fieldLabelChanged: { color: tokens.colorBrandForeground1 },
  changedDot: {
    width: '6px', height: '6px', borderRadius: '50%',
    backgroundColor: tokens.colorBrandBackground, flexShrink: 0,
  },
  hint: { fontSize: '11px', color: tokens.colorNeutralForeground3 },
  searchRow: { display: 'flex', gap: '6px', alignItems: 'center' },
  resultsList: {
    position: 'absolute', zIndex: 100, width: '100%', top: '100%', left: 0,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    marginTop: '4px', maxHeight: '200px', overflowY: 'auto',
    backgroundColor: tokens.colorNeutralBackground1, boxShadow: tokens.shadow8,
  },
  resultItem: {
    padding: '8px 12px', cursor: 'pointer',
    ':hover': { backgroundColor: tokens.colorNeutralBackground3 },
  },
  dayGrid: { display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: '8px' },
  floatingBar: {
    position: 'sticky', bottom: '16px',
    display: 'flex', alignItems: 'center', gap: '10px',
    backgroundColor: '#1f1f1f', borderRadius: '12px',
    padding: '12px 20px',
    boxShadow: '0 8px 32px rgba(0,0,0,.25)',
    marginTop: '8px', zIndex: 10,
  },
  floatingHint: { fontSize: '13px', color: 'rgba(255,255,255,.6)', flex: '1' },
})

// Mapování PascalCase názvů polí z backendu na česky
const FIELD_LABELS: Record<string, string> = {
  CompanyName:        'Název firmy',
  Ico:                'IČO',
  ContactFirstName:   'Jméno kontaktní osoby',
  ContactLastName:    'Příjmení kontaktní osoby',
  ContactEmail:       'Kontaktní e-mail',
  Phone:              'Telefon',
  ExpectedActivities: 'Předpokládané činnosti / role',
  ContractStartDate:     'Začátek platnosti smlouvy',
  CooperationStartDate:  'Zahájení spolupráce',
  ContractEndDate:    'Konec smlouvy',
  ManagerId:          'Odpovědný manažer',
  OrbitEmail:         'ORBIT e-mail',
}

type FieldErrors = Partial<Record<string, string>>

function parseErrors(data: any): { general: string | null; fields: FieldErrors } {
  if (Array.isArray(data) && data[0]?.errorMessage) {
    const fields: FieldErrors = {}
    data.forEach((e: any) => {
      const key = e.propertyName as string
      fields[key] = e.errorMessage
    })
    return { general: null, fields }
  }
  if (typeof data === 'string' && data) return { general: data, fields: {} }
  if (data?.title) return { general: `${data.title}${data.detail ? ': ' + data.detail : ''}`, fields: {} }
  return { general: 'Chyba při ukládání. Zkontrolujte vyplněná pole.', fields: {} }
}

interface FormState extends ContractorForm {
  cooperationStartDate: string
  birthDate: string
  departmentId: number | undefined
  managerName: string
  orbitEmail: string
}

const emptyForm: FormState = {
  companyName:        '',
  ico:                '',
  isVatPayer:         false,
  contactFirstName:   '',
  contactLastName:    '',
  contactEmail:       '',
  phone:              '',
  birthDate:          '',
  expectedActivities: '',
  contractStartDate:     '',
  contractEndDate:       '',
  cooperationStartDate:  '',
  isActive:              true,
  departmentId:       undefined,
  managerId:          undefined,
  orbitEmail:         '',
  note:               '',
  managerName:        '',
}

export default function ContractorFormPage() {
  const styles = useStyles()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEdit = !!id
  const queryClient = useQueryClient()
  const { isHR, isAdmin } = useAuth()
  const canDelete = isHR || isAdmin

  const [form, setForm] = useState<FormState>(emptyForm)
  const [generalError, setGeneralError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [deleteOpen, setDeleteOpen] = useState(false)

  // Detekce neuložených změn
  const savedFormRef = useRef<FormState>(emptyForm)
  const isDirtyRef = useRef(false)
  isDirtyRef.current = JSON.stringify(form) !== JSON.stringify(savedFormRef.current)
  const blocker = useUnsavedChanges(isDirtyRef)

  const isDirty = JSON.stringify(form) !== JSON.stringify(savedFormRef.current)

  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    basic: true, contact: true, contract: true, manager: true, note: true,
  })
  const toggleSection = (key: string) => setOpenSections(s => ({ ...s, [key]: !s[key] }))

  const isChanged = (field: keyof FormState) =>
    JSON.stringify(form[field]) !== JSON.stringify(savedFormRef.current[field])

  const sectionChanged = (...fields: (keyof FormState)[]) => fields.some(f => isChanged(f))

  const cellLabel = (label: string, field?: keyof FormState | (keyof FormState)[]) => {
    const changed = field !== undefined
      ? (Array.isArray(field) ? (field as (keyof FormState)[]).some(f => isChanged(f)) : isChanged(field as keyof FormState))
      : false
    return (
      <span className={mergeClasses(styles.fieldLabel, changed ? styles.fieldLabelChanged : undefined)}>
        {changed && <span className={styles.changedDot} />}
        {label}
      </span>
    )
  }

  const permanentDeleteMutation = useMutation({
    mutationFn: () => contractorsService.permanentDelete(Number(id)),
    onSuccess: () => {
      isDirtyRef.current = false
      queryClient.invalidateQueries({ queryKey: ['contractors'] })
      navigate('/contractors')
    },
    onError: (err: any) => {
      const msg = err?.response?.data && typeof err.response.data === 'string'
        ? err.response.data
        : 'Externistu se nepodařilo trvale smazat.'
      setGeneralError(msg)
      setDeleteOpen(false)
    },
  })

  const { data: departments = [] } = useQuery({
    queryKey: ['departments'],
    queryFn: referenceDataService.getDepartments,
  })

  // Vyhledávání manažera
  const [managerQuery, setManagerQuery] = useState('')
  const [managerResults, setManagerResults] = useState<{ id: number; fullName: string; departmentName: string | null }[]>([])
  const [isSearchingManager, setIsSearchingManager] = useState(false)

  // Vyhledávání ORBIT e-mailu v Entra ID
  const [orbitEmailQuery, setOrbitEmailQuery] = useState('')
  const [orbitEmailResults, setOrbitEmailResults] = useState<{ displayName: string; mail: string | null; upn: string }[]>([])
  const [isSearchingOrbitEmail, setIsSearchingOrbitEmail] = useState(false)
  const [orbitEmailConflict, setOrbitEmailConflict] = useState<string | null>(null)

  const { data: detail, isLoading } = useQuery({
    queryKey: ['contractor', id],
    queryFn: () => contractorsService.getById(Number(id)),
    enabled: isEdit,
  })

  useEffect(() => {
    if (detail) {
      const loaded: FormState = {
        companyName:        detail.companyName,
        ico:                detail.ico,
        isVatPayer:         detail.isVatPayer ?? false,
        contactFirstName:   detail.contactFirstName,
        contactLastName:    detail.contactLastName,
        contactEmail:       detail.contactEmail,
        phone:              detail.phone ?? '',
        birthDate:          detail.birthDate?.split('T')[0] ?? '',
        expectedActivities: detail.expectedActivities ?? '',
        contractStartDate:    detail.contractStartDate?.split('T')[0] ?? detail.contractStartDate ?? '',
        contractEndDate:      detail.contractEndDate?.split('T')[0] ?? '',
        cooperationStartDate: detail.cooperationStartDate?.split('T')[0] ?? '',
        isActive:           detail.isActive,
        departmentId:       detail.departmentId ?? undefined,
        managerId:          detail.managerId ?? undefined,
        orbitEmail:         detail.orbitEmail ?? '',
        note:               detail.note ?? '',
        managerName:        detail.managerFullName ?? '',
      }
      setForm(loaded)
      savedFormRef.current = loaded
    }
  }, [detail])

  const handleManagerSearch = async () => {
    if (managerQuery.length < 2) return
    setIsSearchingManager(true)
    try {
      const res = await employeesService.getAll({ search: managerQuery, pageSize: 10, excludeTerminated: true })
      setManagerResults(res.items)
    } catch {
      setManagerResults([])
    } finally {
      setIsSearchingManager(false)
    }
  }

  const handleSelectManager = (emp: { id: number; fullName: string; departmentName: string | null }) => {
    setForm(f => ({ ...f, managerId: emp.id, managerName: emp.fullName }))
    setManagerResults([])
    setManagerQuery('')
  }

  const handleOrbitEmailSearch = async () => {
    if (orbitEmailQuery.length < 2) return
    setIsSearchingOrbitEmail(true)
    try {
      const users = await searchEntraUsers(orbitEmailQuery)
      setOrbitEmailResults(users.map(u => ({
        displayName: u.displayName,
        mail: u.mail,
        upn: u.userPrincipalName,
      })))
    } catch {
      setOrbitEmailResults([])
    } finally {
      setIsSearchingOrbitEmail(false)
    }
  }

  const handleSelectOrbitEmail = async (user: { displayName: string; mail: string | null; upn: string }) => {
    const email = user.mail ?? user.upn
    setOrbitEmailConflict(null)
    setForm(f => ({ ...f, orbitEmail: email }))
    setOrbitEmailResults([])
    setOrbitEmailQuery('')
    try {
      const check = await employeesService.checkEmail(email)
      if (check.isTaken) {
        setOrbitEmailConflict(`E-mail patří aktivnímu zaměstnanci ${check.employeeName}.`)
      }
    } catch {
      // Pokud kontrola selže, backend validace zabrání uložení
    }
  }

  const set = (field: keyof FormState) => (e: any) => {
    const value = e.target?.value ?? e
    setForm(f => ({ ...f, [field]: value }))
    // Vymaž chybu pro toto pole při editaci
    const backendKey = Object.entries(FIELD_LABELS).find(
      ([, label]) => label === FIELD_LABELS[field] || field.toLowerCase() === field
    )?.[0]
    if (backendKey) setFieldErrors(prev => { const n = { ...prev }; delete n[backendKey]; return n })
  }

  const fieldErr = (backendKey: string) => fieldErrors[backendKey] || undefined

  const mutation = useMutation({
    mutationFn: (data: ContractorForm & { isActive: boolean }) =>
      isEdit
        ? contractorsService.update(Number(id), data)
        : contractorsService.create(data),
    onSuccess: (result: any) => {
      isDirtyRef.current = false
      queryClient.invalidateQueries({ queryKey: ['contractors'] })
      queryClient.invalidateQueries({ queryKey: ['contractor', id] })
      const newId = isEdit ? id : result?.id
      navigate(newId ? `/contractors/${newId}` : '/contractors')
    },
    onError: (err: any) => {
      const { general, fields } = parseErrors(err?.response?.data)
      setGeneralError(general)
      setFieldErrors(fields)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setGeneralError(null)
    setFieldErrors({})

    if (form.contractEndDate && form.contractStartDate && form.contractEndDate < form.contractStartDate) {
      setGeneralError('Datum ukončení nemůže být dříve než datum zahájení.')
      return
    }

    mutation.mutate({
      companyName:        form.companyName,
      ico:                form.ico,
      isVatPayer:         form.isVatPayer ?? false,
      contactFirstName:   form.contactFirstName,
      contactLastName:    form.contactLastName,
      contactEmail:       form.contactEmail,
      phone:              form.phone || undefined,
      birthDate:          form.birthDate || undefined,
      expectedActivities: form.expectedActivities || undefined,
      contractStartDate:    form.contractStartDate,
      contractEndDate:      form.contractEndDate || undefined,
      cooperationStartDate: form.cooperationStartDate || undefined,
      isActive:           form.isActive ?? true,
      departmentId:       form.departmentId,
      managerId:          form.managerId,
      orbitEmail:         form.orbitEmail || undefined,
      note:               form.note || undefined,
    })
  }

  if (isEdit && isLoading) return <Spinner label="Načítám..." />

  return (
    <div style={{ maxWidth: 800 }}>
      {/* Page header */}
      <div className={styles.pageHeader}>
        <Button appearance="subtle" icon={<ArrowLeft24Regular />} onClick={() => navigate('/contractors')}>
          Zpět
        </Button>
        <Text size={600} weight="semibold" style={{ flex: 1 }}>
          {isEdit ? 'Upravit externistu' : 'Přidat externistu'}
        </Text>
      </div>

      <form onSubmit={handleSubmit}>
        {generalError && (
          <MessageBar intent="error" style={{ marginBottom: '16px' }}>
            <MessageBarBody>{generalError}</MessageBarBody>
          </MessageBar>
        )}

        {/* ── Základní informace ── */}
        <div className={styles.card}>
          {sectionChanged('companyName', 'ico', 'isVatPayer') && (
            <div className={styles.changedStripe} />
          )}
          <div className={styles.cardHeader} onClick={() => toggleSection('basic')}>
            <span className={styles.cardTitle}>Základní informace</span>
            {openSections.basic ? <ChevronUp20Regular /> : <ChevronDown20Regular />}
          </div>
          {openSections.basic && (
            <>
              {/* Řádek FULL: Název firmy */}
              <div className={mergeClasses(styles.fieldRow, styles.fieldRowFull)}>
                <div className={styles.fieldCell}>
                  {cellLabel('Název firmy *', 'companyName')}
                  <Input value={form.companyName} onChange={set('companyName')} />
                  {fieldErr('CompanyName') && (
                    <span style={{ color: tokens.colorPaletteRedForeground1, fontSize: '11px' }}>{fieldErr('CompanyName')}</span>
                  )}
                </div>
              </div>

              {/* Řádek: IČO | Plátce DPH */}
              <div className={styles.fieldRow}>
                <div className={mergeClasses(styles.fieldCell, styles.fieldCellBorderRight)}>
                  {cellLabel('IČO *', 'ico')}
                  <Input
                    value={form.ico}
                    onChange={set('ico')}
                    placeholder="např. 12345678 nebo VAT123456"
                  />
                  {fieldErr('Ico') && (
                    <span style={{ color: tokens.colorPaletteRedForeground1, fontSize: '11px' }}>{fieldErr('Ico')}</span>
                  )}
                </div>
                <div className={styles.fieldCell}>
                  {cellLabel('Plátce DPH', 'isVatPayer')}
                  <Switch
                    label={form.isVatPayer ? 'Ano' : 'Ne'}
                    checked={form.isVatPayer ?? false}
                    onChange={(_, d) => setForm(f => ({ ...f, isVatPayer: d.checked }))}
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {/* ── Kontaktní osoba ── */}
        <div className={styles.card}>
          {sectionChanged('contactFirstName', 'contactLastName', 'contactEmail', 'phone', 'birthDate', 'cooperationStartDate') && (
            <div className={styles.changedStripe} />
          )}
          <div className={styles.cardHeader} onClick={() => toggleSection('contact')}>
            <span className={styles.cardTitle}>Kontaktní osoba</span>
            {openSections.contact ? <ChevronUp20Regular /> : <ChevronDown20Regular />}
          </div>
          {openSections.contact && (
            <>
              {/* Řádek: Jméno | Příjmení */}
              <div className={styles.fieldRow}>
                <div className={mergeClasses(styles.fieldCell, styles.fieldCellBorderRight)}>
                  {cellLabel('Jméno *', 'contactFirstName')}
                  <Input value={form.contactFirstName} onChange={set('contactFirstName')} />
                  {fieldErr('ContactFirstName') && (
                    <span style={{ color: tokens.colorPaletteRedForeground1, fontSize: '11px' }}>{fieldErr('ContactFirstName')}</span>
                  )}
                </div>
                <div className={styles.fieldCell}>
                  {cellLabel('Příjmení *', 'contactLastName')}
                  <Input value={form.contactLastName} onChange={set('contactLastName')} />
                  {fieldErr('ContactLastName') && (
                    <span style={{ color: tokens.colorPaletteRedForeground1, fontSize: '11px' }}>{fieldErr('ContactLastName')}</span>
                  )}
                </div>
              </div>

              {/* Řádek: Kontaktní e-mail | Telefon */}
              <div className={styles.fieldRow}>
                <div className={mergeClasses(styles.fieldCell, styles.fieldCellBorderRight)}>
                  {cellLabel('Kontaktní e-mail *', 'contactEmail')}
                  <Input
                    type="email"
                    value={form.contactEmail}
                    onChange={(_, d) => setForm(f => ({ ...f, contactEmail: d.value }))}
                  />
                  {fieldErr('ContactEmail') && (
                    <span style={{ color: tokens.colorPaletteRedForeground1, fontSize: '11px' }}>{fieldErr('ContactEmail')}</span>
                  )}
                </div>
                <div className={styles.fieldCell}>
                  {cellLabel('Telefon', 'phone')}
                  <CzPhoneInput value={form.phone ?? ''} onChange={v => setForm(f => ({ ...f, phone: v }))} />
                  {fieldErr('Phone') && (
                    <span style={{ color: tokens.colorPaletteRedForeground1, fontSize: '11px' }}>{fieldErr('Phone')}</span>
                  )}
                </div>
              </div>

              {/* Řádek: Datum narození | Zahájení spolupráce */}
              <div className={styles.fieldRow}>
                <div className={mergeClasses(styles.fieldCell, styles.fieldCellBorderRight)}>
                  {cellLabel('Datum narození', 'birthDate')}
                  <NativeDateInput value={form.birthDate ?? ''} onChange={v => setForm(f => ({ ...f, birthDate: v }))} />
                </div>
                <div className={styles.fieldCell}>
                  {cellLabel('Zahájení spolupráce', 'cooperationStartDate')}
                  <NativeDateInput value={form.cooperationStartDate ?? ''} onChange={v => setForm(f => ({ ...f, cooperationStartDate: v }))} />
                  {fieldErr('CooperationStartDate') && (
                    <span style={{ color: tokens.colorPaletteRedForeground1, fontSize: '11px' }}>{fieldErr('CooperationStartDate')}</span>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* ── Smlouva ── */}
        <div className={styles.card}>
          {sectionChanged('contractStartDate', 'contractEndDate', 'expectedActivities', 'isActive') && (
            <div className={styles.changedStripe} />
          )}
          <div className={styles.cardHeader} onClick={() => toggleSection('contract')}>
            <span className={styles.cardTitle}>Smlouva</span>
            {openSections.contract ? <ChevronUp20Regular /> : <ChevronDown20Regular />}
          </div>
          {openSections.contract && (
            <>
              {/* Řádek: Začátek platnosti smlouvy | Konec smlouvy */}
              <div className={styles.fieldRow}>
                <div className={mergeClasses(styles.fieldCell, styles.fieldCellBorderRight)}>
                  {cellLabel('Začátek platnosti smlouvy *', 'contractStartDate')}
                  <NativeDateInput value={form.contractStartDate} onChange={v => setForm(f => ({ ...f, contractStartDate: v }))} />
                  {fieldErr('ContractStartDate') && (
                    <span style={{ color: tokens.colorPaletteRedForeground1, fontSize: '11px' }}>{fieldErr('ContractStartDate')}</span>
                  )}
                </div>
                <div className={styles.fieldCell}>
                  {cellLabel('Konec smlouvy / ukončení spolupráce', 'contractEndDate')}
                  <NativeDateInput value={form.contractEndDate ?? ''} onChange={v => setForm(f => ({ ...f, contractEndDate: v }))} min={form.contractStartDate || undefined} />
                  {fieldErr('ContractEndDate') && (
                    <span style={{ color: tokens.colorPaletteRedForeground1, fontSize: '11px' }}>{fieldErr('ContractEndDate')}</span>
                  )}
                </div>
              </div>

              {/* Řádek: Stav (jen isEdit) */}
              {isEdit && (
                <div className={styles.fieldRow}>
                  <div className={mergeClasses(styles.fieldCell, styles.fieldCellBorderRight)}>
                    {cellLabel('Stav', 'isActive')}
                    <Switch
                      label={form.isActive ? 'Aktivní' : 'Neaktivní'}
                      checked={form.isActive ?? true}
                      onChange={(_, d) => setForm(f => ({ ...f, isActive: d.checked }))}
                    />
                  </div>
                  <div className={styles.fieldCell} />
                </div>
              )}

              {/* Řádek FULL: Předpokládané činnosti / role */}
              <div className={mergeClasses(styles.fieldRow, styles.fieldRowFull)}>
                <div className={styles.fieldCell}>
                  {cellLabel('Předpokládané činnosti / role', 'expectedActivities')}
                  <Input
                    value={form.expectedActivities ?? ''}
                    onChange={set('expectedActivities')}
                    placeholder="např. Vývoj SW, Poradenství..."
                  />
                  {fieldErr('ExpectedActivities') && (
                    <span style={{ color: tokens.colorPaletteRedForeground1, fontSize: '11px' }}>{fieldErr('ExpectedActivities')}</span>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* ── Odpovědný manažer ── */}
        <div className={styles.card}>
          {sectionChanged('departmentId', 'managerId', 'orbitEmail') && (
            <div className={styles.changedStripe} />
          )}
          <div className={styles.cardHeader} onClick={() => toggleSection('manager')}>
            <span className={styles.cardTitle}>Odpovědný manažer</span>
            {openSections.manager ? <ChevronUp20Regular /> : <ChevronDown20Regular />}
          </div>
          {openSections.manager && (
            <>
              {/* Řádek: Oddělení | Manažer */}
              <div className={styles.fieldRow}>
                <div className={mergeClasses(styles.fieldCell, styles.fieldCellBorderRight)}>
                  {cellLabel('Oddělení', 'departmentId')}
                  <Combobox
                    placeholder="— vyberte oddělení —"
                    value={departments.find(d => d.id === form.departmentId)?.name ?? ''}
                    selectedOptions={form.departmentId ? [String(form.departmentId)] : []}
                    onOptionSelect={(_, d) => setForm(f => ({ ...f, departmentId: d.optionValue ? Number(d.optionValue) : undefined }))}
                    onInput={e => { if (!(e.target as HTMLInputElement).value) setForm(f => ({ ...f, departmentId: undefined })) }}
                  >
                    {departments.map(d => <Option key={d.id} value={String(d.id)}>{d.name}</Option>)}
                  </Combobox>
                </div>
                <div className={styles.fieldCell}>
                  {cellLabel('Manažer', 'managerId')}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', position: 'relative' }}>
                    {form.managerId ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Text>{form.managerName}</Text>
                        <Button
                          size="small" appearance="subtle"
                          onClick={() => setForm(f => ({ ...f, managerId: undefined, managerName: '' }))}
                        >✕</Button>
                      </div>
                    ) : (
                      <div className={styles.searchRow}>
                        <Input
                          value={managerQuery}
                          onChange={(_, d) => setManagerQuery(d.value)}
                          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleManagerSearch())}
                          placeholder="Hledat zaměstnance..."
                          style={{ flex: 1 }}
                        />
                        <Button
                          icon={isSearchingManager ? <Spinner size="tiny" /> : <PersonSearch24Regular />}
                          onClick={handleManagerSearch}
                          disabled={isSearchingManager || managerQuery.length < 2}
                        />
                      </div>
                    )}
                    {managerResults.length > 0 && (
                      <div className={styles.resultsList}>
                        {managerResults.map(emp => (
                          <div key={emp.id} className={styles.resultItem} onClick={() => handleSelectManager(emp)}>
                            <Text weight="semibold">{emp.fullName}</Text>
                            {emp.departmentName && (
                              <Text size={200} style={{ display: 'block', color: tokens.colorNeutralForeground3 }}>
                                {emp.departmentName}
                              </Text>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Řádek: ORBIT e-mail */}
              <div className={mergeClasses(styles.fieldRow, styles.fieldRowFull)}>
                <div className={styles.fieldCell}>
                  {cellLabel('ORBIT e-mail', 'orbitEmail')}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', position: 'relative' }}>
                    {form.orbitEmail ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Text>{form.orbitEmail}</Text>
                        <Button size="small" appearance="subtle" onClick={() => { setForm(f => ({ ...f, orbitEmail: '' })); setOrbitEmailConflict(null) }}>✕</Button>
                      </div>
                    ) : (
                      <div className={styles.searchRow}>
                        <Input
                          value={orbitEmailQuery}
                          onChange={(_, d) => setOrbitEmailQuery(d.value)}
                          onKeyDown={e => e.key === 'Enter' && !IS_MOCK && (e.preventDefault(), handleOrbitEmailSearch())}
                          placeholder="Hledat v Entra ID..."
                          style={{ flex: 1 }}
                          disabled={IS_MOCK}
                        />
                        {!IS_MOCK && (
                          <Button
                            icon={isSearchingOrbitEmail ? <Spinner size="tiny" /> : <PersonSearch24Regular />}
                            onClick={handleOrbitEmailSearch}
                            disabled={isSearchingOrbitEmail || orbitEmailQuery.length < 2}
                          />
                        )}
                      </div>
                    )}
                    {!IS_MOCK && !form.orbitEmail && (
                      <span className={styles.hint}>Vyhledejte uživatele v Entra ID</span>
                    )}
                    {orbitEmailResults.length > 0 && (
                      <div className={styles.resultsList}>
                        {orbitEmailResults.map(u => (
                          <div key={u.upn} className={styles.resultItem} onClick={() => handleSelectOrbitEmail(u)}>
                            <Text weight="semibold">{u.displayName}</Text>
                            <Text size={200} style={{ display: 'block', color: tokens.colorNeutralForeground3 }}>
                              {u.mail ?? u.upn}
                            </Text>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {orbitEmailConflict && (
                    <span style={{ color: tokens.colorPaletteYellowForeground1, fontSize: '11px' }}>{orbitEmailConflict}</span>
                  )}
                  {fieldErr('OrbitEmail') && (
                    <span style={{ color: tokens.colorPaletteRedForeground1, fontSize: '11px' }}>{fieldErr('OrbitEmail')}</span>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* ── Poznámka (jen HR/Admin) ── */}
        {(isHR || isAdmin) && (
          <div className={styles.card}>
            {sectionChanged('note') && (
              <div className={styles.changedStripe} />
            )}
            <div className={styles.cardHeader} onClick={() => toggleSection('note')}>
              <span className={styles.cardTitle}>Poznámka</span>
              {openSections.note ? <ChevronUp20Regular /> : <ChevronDown20Regular />}
            </div>
            {openSections.note && (
              <>
                {/* Řádek FULL: Interní poznámka */}
                <div className={mergeClasses(styles.fieldRow, styles.fieldRowFull)}>
                  <div className={styles.fieldCell}>
                    {cellLabel('Interní poznámka', 'note')}
                    <Textarea
                      value={form.note ?? ''}
                      onChange={(_, d) => setForm(f => ({ ...f, note: d.value }))}
                      rows={4}
                      resize="vertical"
                      style={{ width: '100%' }}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Floating bar */}
        <div className={styles.floatingBar}>
          {/* Trvale odstranit — jen když je uložený stav neaktivní */}
          {isEdit && canDelete && detail?.isActive === false && (
            <Button
              appearance="subtle"
              icon={<Delete24Regular />}
              type="button"
              style={{ color: '#f87171' }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.15)')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
              onClick={() => setDeleteOpen(true)}
            >
              Trvale odstranit
            </Button>
          )}
          <span className={styles.floatingHint}>
            {isDirty ? '✏ Neuložené změny' : ''}
          </span>
          {/* Kopírovat záznam — jen v edit módu */}
          {isEdit && canDelete && (
            <Button
              appearance="subtle"
              icon={<DocumentCopy24Regular />}
              type="button"
              style={{ color: 'rgba(255,255,255,0.8)' }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.12)')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
              onClick={() => navigate('/contractors/new', { state: { copyFrom: form } })}
            >
              Kopírovat záznam
            </Button>
          )}
          <Button
            appearance="subtle"
            type="button"
            style={{ color: 'rgba(255,255,255,0.8)' }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.12)')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
            onClick={() => navigate('/contractors')}
          >
            Zrušit editaci
          </Button>
          <Button
            appearance="primary"
            icon={mutation.isPending ? <Spinner size="tiny" /> : <Save24Regular />}
            type="submit"
            disabled={mutation.isPending}
          >
            Uložit
          </Button>
        </div>
      </form>

      {/* Dialog: trvalé smazání */}
      <Dialog open={deleteOpen} onOpenChange={(_, s) => setDeleteOpen(s.open)}>
        <DialogSurface>
          <DialogTitle>Trvale odstranit externistu</DialogTitle>
          <DialogBody>
            <DialogContent>
              Opravdu chcete trvale odstranit externistu <strong>{form.companyName}</strong>?
              Tato akce je nevratná a smaže všechna přidružená data.
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeleteOpen(false)}>Zrušit</Button>
              <Button
                appearance="primary"
                style={{ backgroundColor: tokens.colorPaletteRedBackground3, color: '#fff' }}
                icon={permanentDeleteMutation.isPending ? <Spinner size="tiny" /> : <Delete24Regular />}
                disabled={permanentDeleteMutation.isPending}
                onClick={() => permanentDeleteMutation.mutate()}
              >
                Trvale odstranit
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Dialog: neuložené změny */}
      <UnsavedChangesDialog blocker={blocker} />
    </div>
  )
}
